import {useLazyQuery} from "@apollo/client";
import {useEffect, useState} from "react";
import {Outlet, useNavigate, useSearchParams} from "react-router-dom";
import {
  Button,
  Backdrop,
  CircularProgress,
  Paper,
  Stack,
  TextField
} from "@mui/material";

import {LOOKUP_PARENT_WITH_CHILDREN} from "../queries/GraphQLQueries";
import CustomAlert from "../components/CustomAlert";
import ParentInfoDetailsDisplay from "../components/member-lookup/ParentInfoDetailsDisplay";
import {NAVIGATION_PATHS} from "../config/NavigationPaths";

const KEY_RETURN = 'Enter';

export const buildMemberLookupUrl = (memberId) => `${NAVIGATION_PATHS.MEMBER_LOOKUP}?memberId=${memberId}`;

export default function ParentInfoLookupView() {

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const routeMemberId = searchParams.get('memberId');

  const [lookupParent, {loading, error, data}] = useLazyQuery(LOOKUP_PARENT_WITH_CHILDREN, {fetchPolicy: 'network-only'});
  const [externalId, setExternalId] = useState(routeMemberId || "");
  const [parent, setParent] =  useState();
  const [lookupEnabled, setLookupEnabled] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [userTriggeredManualRefresh, setUserTriggeredManualRefresh] = useState(false);

  useEffect(() => {
      if (routeMemberId || userTriggeredManualRefresh){
        lookupMember();
        setUserTriggeredManualRefresh(false);
      }
  }, [userTriggeredManualRefresh]);

  useEffect(() => {
    setLookupEnabled(externalId !== "");
  }, [externalId, parent]);

  useEffect(() => {
    if (data?.parentInfo) {
      setParent(data.parentInfo);
    }
  }, [data]);

  const handleCloseCustomAlert = () => {
    setAlertMessage(null);
  };

  function handleExternalIdFieldChanged(e) {
    setExternalId(e.target.value.trim());
  }

  function lookupMember() {
    lookupParent({variables: {externalId: externalId}})
        .then((result) => {
          if (!result?.data.parentInfo) {
            setAlertMessage({ children: "Member id '" + externalId + "' could not be found.", severity: 'warning' });
          }
        })
        .catch((e) => {
          setAlertMessage({ children: "Error fetching member record " + e.message, severity: 'error' });
          console.log("error " + e)
        });
  }

  function handleClearClicked() {
    setParent(null);
    setExternalId('');
    navigate("/member-lookup");
  }

  return (
      <>
      <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
      <Stack sx={{m: 1}} spacing={2}>

        {!parent
            &&
          <Paper sx={{p: 2}} elevation={4}>
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
              <TextField 
                id="externalId" 
                label="External Id / Member Id" 
                variant="standard" 
                value={externalId}
                onChange={handleExternalIdFieldChanged}
                onKeyDown={(e) => ( e.key === KEY_RETURN ? lookupMember(e) : null )} />
              <Button variant="contained" disabled={!lookupEnabled} onClick={lookupMember}>Lookup</Button>
            </Stack>
          </Paper>
        }

        {parent
            && <ParentInfoDetailsDisplay 
                  parent={parent} 
                  onClearClickedHandler={handleClearClicked} 
                  userTriggeredManualRefresh={userTriggeredManualRefresh} 
                  handleUserTriggeredManualRefresh={setUserTriggeredManualRefresh} />
        }

        <Outlet />

      </Stack>
      </>);
};